import React, { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Chart from "react-apexcharts";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import toast from "react-hot-toast";
import {
  TrendingUp,
  ShoppingCart,
  Users,
  Package,
  Store,
  IndianRupee,
  Calendar,
  Filter,
  Download,
  RotateCcw,
  Bookmark,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
  FileText,
  Clock,
  Settings,
  Layers,
  ArrowRight,
  MapPin,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Search,
  ExternalLink,
  ChevronRight,
  ShieldCheck,
  Send,
  Eye,
  SlidersHorizontal,
  Printer,
  FolderOpen
} from "lucide-react";
import ReportDetailView from "./ReportDetailView";
import { MASTER_89_REPORTS } from "./reportCatalogData";
import StandardReportScreen from "./StandardReportScreen";
import ReportCatalogBrowser from "./ReportCatalogBrowser";

/* ------------------------------------------------------------------ */
/*  STATIC / INITIAL DATA EXACTLY MATCHING THE USER'S SCREENSHOT        */
/* ------------------------------------------------------------------ */

export const KPI_DATA = [
  {
    id: "sales",
    label: "Total Sales",
    value: "₹ 18,56,320",
    change: "+ 18%",
    isPositive: true,
    subtext: "vs previous period",
    icon: BarChart3,
    iconBg: "bg-emerald-500 text-white",
    cardBorder: "border-emerald-100",
  },
  {
    id: "orders",
    label: "Total Orders",
    value: "1,245",
    change: "+ 12%",
    isPositive: true,
    subtext: "vs previous period",
    icon: ShoppingCart,
    iconBg: "bg-[#b07d17] text-white",
    cardBorder: "border-amber-100",
  },
  {
    id: "customers",
    label: "Total Customers",
    value: "892",
    change: "+ 10%",
    isPositive: true,
    subtext: "vs previous period",
    icon: Users,
    iconBg: "bg-rose-500 text-white",
    cardBorder: "border-rose-100",
  },
  {
    id: "products_sold",
    label: "Total Products Sold",
    value: "3,428",
    change: "+ 15%",
    isPositive: true,
    subtext: "vs previous period",
    icon: Package,
    iconBg: "bg-amber-500 text-white",
    cardBorder: "border-amber-100",
  },
  {
    id: "sellers",
    label: "Total Sellers",
    value: "156",
    change: "+ 8%",
    isPositive: true,
    subtext: "vs previous period",
    icon: Store,
    iconBg: "bg-purple-600 text-white",
    cardBorder: "border-purple-100",
  },
  {
    id: "commission",
    label: "Total Commission",
    value: "₹ 2,45,380",
    change: "+ 14%",
    isPositive: true,
    subtext: "vs previous period",
    icon: IndianRupee,
    iconBg: "bg-[#d4af37] text-slate-900",
    cardBorder: "border-amber-100",
  },
];

export const CATEGORY_SALES = [
  { name: "Handloom", pct: 28.5, amount: "₹ 5,29,051", color: "#b07d17" },
  { name: "Home Decor", pct: 18.2, amount: "₹ 3,37,850", color: "#10b981" },
  { name: "Textiles", pct: 15.6, amount: "₹ 2,89,585", color: "#c59a3f" },
  { name: "Pottery", pct: 12.4, amount: "₹ 2,30,183", color: "#e5c158" },
  { name: "Metal Crafts", pct: 8.6, amount: "₹ 1,59,643", color: "#8b5cf6" },
  { name: "Wood Crafts", pct: 7.1, amount: "₹ 1,31,798", color: "#f97316" },
  { name: "Stone Crafts", pct: 5.3, amount: "₹ 98,384", color: "#64748b" },
  { name: "Others", pct: 4.3, amount: "₹ 79,826", color: "#94a3b8" },
];

export const PAYMENT_METHODS = [
  { name: "UPI", pct: 28.5, amount: "₹ 5,29,051", color: "#b07d17" },
  { name: "Credit Card", pct: 24.8, amount: "₹ 4,60,367", color: "#10b981" },
  { name: "Debit Card", pct: 15.6, amount: "₹ 2,89,585", color: "#c59a3f" },
  { name: "Net Banking", pct: 9.2, amount: "₹ 1,70,781", color: "#e5c158" },
  { name: "COD", pct: 6.1, amount: "₹ 1,13,235", color: "#f97316" },
  { name: "Wallet", pct: 1.8, amount: "₹ 33,413", color: "#8b5cf6" },
  { name: "EMI / Pay Later", pct: 1.5, amount: "₹ 27,844", color: "#ec4899" },
  { name: "Others", pct: 12.5, amount: "₹ 2,32,044", color: "#64748b" },
];

export const TOP_PRODUCTS = [
  {
    id: 1,
    name: "Handwoven Cotton Saree",
    category: "Handloom",
    qty: 245,
    amount: "6,12,105",
    pct: "32.9%",
    img: "https://images.unsplash.com/photo-1610030469983-98e550d615ef?auto=format&fit=crop&w=120&q=80",
  },
  {
    id: 2,
    name: "Terracotta Diya Set (6 pcs)",
    category: "Pottery",
    qty: 189,
    amount: "3,02,511",
    pct: "16.3%",
    img: "https://images.unsplash.com/photo-1606744888344-493238955de0?auto=format&fit=crop&w=120&q=80",
  },
  {
    id: 3,
    name: "Wooden Wall Art",
    category: "Wood Crafts",
    qty: 152,
    amount: "1,90,000",
    pct: "10.2%",
    img: "https://images.unsplash.com/photo-1513519245088-0e12902e5a38?auto=format&fit=crop&w=120&q=80",
  },
  {
    id: 4,
    name: "Ikat Dupatta",
    category: "Textiles",
    qty: 128,
    amount: "1,66,400",
    pct: "9.0%",
    img: "https://images.unsplash.com/photo-1601934988636-f83196f7e4da?auto=format&fit=crop&w=120&q=80",
  },
  {
    id: 5,
    name: "Brass Pooja Diya Set",
    category: "Metal Crafts",
    qty: 96,
    amount: "1,43,904",
    pct: "7.8%",
    img: "https://images.unsplash.com/photo-1610427845722-e42d765fb18a?auto=format&fit=crop&w=120&q=80",
  },
];

export const TOP_SELLERS = [
  { id: 1, name: "Odisha Handlooms", orders: 45, sales: "1,25,680", comm: "12,568" },
  { id: 2, name: "Rajasthan Crafts", orders: 38, sales: "98,450", comm: "9,845" },
  { id: 3, name: "Assam Weaves", orders: 32, sales: "76,230", comm: "7,623" },
  { id: 4, name: "Tribal Artisans", orders: 28, sales: "65,440", comm: "6,544" },
  { id: 5, name: "North East Crafts", orders: 24, sales: "52,190", comm: "5,219" },
];

export const TOP_CUSTOMERS = [
  { id: 1, name: "Priya Sharma", orders: 12, spent: "24,560" },
  { id: 2, name: "Rahul Mehta", orders: 9, spent: "18,790" },
  { id: 3, name: "Anita Desai", orders: 8, spent: "16,320" },
  { id: 4, name: "Vikram Singh", orders: 7, spent: "12,450" },
  { id: 5, name: "Neha Kapoor", orders: 6, spent: "10,890" },
];

export const ORDER_STATUS_DIST = [
  { label: "Delivered", pct: 68.4, color: "#10b981" },
  { label: "Shipped", pct: 14.2, color: "#b07d17" },
  { label: "Processing", pct: 8.6, color: "#e5c158" },
  { label: "Pending", pct: 4.5, color: "#f97316" },
  { label: "Cancelled", pct: 3.1, color: "#ef4444" },
  { label: "Returned", pct: 1.2, color: "#8b5cf6" },
];

export const REGION_ORDERS = [
  { state: "Odisha", pct: 28.4 },
  { state: "Maharashtra", pct: 12.6 },
  { state: "Delhi", pct: 10.2 },
  { state: "Karnataka", pct: 8.5 },
  { state: "Tamil Nadu", pct: 7.8 },
  { state: "West Bengal", pct: 6.9 },
  { state: "Others", pct: 25.6 },
];

// Mapping of sub-report keys to display titles
export const REPORT_TYPES = [
  { id: "dashboard", label: "Reports Dashboard", path: "/admin-reports" },
  { id: "sales", label: "Sales Report", path: "/admin-reports/sales" },
  { id: "orders", label: "Order Report", path: "/admin-reports/orders" },
  { id: "products", label: "Product Report", path: "/admin-reports/products" },
  { id: "sellers", label: "Seller Report", path: "/admin-reports/sellers" },
  { id: "customers", label: "Customer Report", path: "/admin-reports/customers" },
  { id: "inventory", label: "Inventory Report", path: "/admin-reports/inventory" },
  { id: "purchase", label: "Purchase Report", path: "/admin-reports/purchase" },
  { id: "payments", label: "Payment & Settlement", path: "/admin-reports/payments" },
  { id: "refunds", label: "Refunds Report", path: "/admin-reports/refunds" },
  { id: "tax", label: "Tax Report (GST)", path: "/admin-reports/tax" },
  { id: "marketing", label: "Marketing Report", path: "/admin-reports/marketing" },
  { id: "shipping", label: "Shipping Report", path: "/admin-reports/shipping" },
  { id: "custom", label: "Custom Report", path: "/admin-reports/custom" },
];

/* ------------------------------------------------------------------ */
/*  MAIN COMPONENT                                                     */
/* ------------------------------------------------------------------ */
const AdminReportsDashboard = ({ type = "dashboard" }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Active sub-report selection
  const currentReportType = useMemo(() => {
    const found = REPORT_TYPES.find((r) => r.id === type || location.pathname === r.path);
    return found ? found.id : "dashboard";
  }, [type, location.pathname]);

  // Filters State matching screenshot
  const [dateRange, setDateRange] = useState("01 Oct 2026 - 07 Oct 2026");
  const [selectedReportType, setSelectedReportType] = useState(
    REPORT_TYPES.find((r) => r.id === currentReportType)?.label || "Reports Dashboard"
  );
  const [groupBy, setGroupBy] = useState("Daily");
  const [category, setCategory] = useState("All Categories");
  const [seller, setSeller] = useState("All Sellers");
  const [customer, setCustomer] = useState("All Customers");
  const [product, setProduct] = useState("All Products");
  const [paymentMethod, setPaymentMethod] = useState("All Payment Methods");
  const [orderStatus, setOrderStatus] = useState("All Status");
  const [shippingZone, setShippingZone] = useState("All Zones");
  const [country, setCountry] = useState("All Countries");
  const [stateRegion, setStateRegion] = useState("All States");

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [activeDateTab, setActiveDateTab] = useState("Last 7 Days");
  const [activeCatalogView, setActiveCatalogView] = useState(false);
  const [selectedCatalogReport, setSelectedCatalogReport] = useState(null);

  const matchedReport = useMemo(() => {
    if (type === "sales") return MASTER_89_REPORTS.find((r) => r.key === "daily-sales") || MASTER_89_REPORTS[1];
    if (type === "orders") return MASTER_89_REPORTS.find((r) => r.key === "order-register") || MASTER_89_REPORTS[8];
    if (type === "products") return MASTER_89_REPORTS.find((r) => r.key === "product-sales") || MASTER_89_REPORTS[3];
    if (type === "sellers") return MASTER_89_REPORTS.find((r) => r.key === "seller-master-report") || MASTER_89_REPORTS[18];
    if (type === "customers") return MASTER_89_REPORTS.find((r) => r.key === "customer-registration-report") || MASTER_89_REPORTS[13];
    if (type === "inventory") return MASTER_89_REPORTS.find((r) => r.key === "inventory-stock-report") || MASTER_89_REPORTS[32];
    if (type === "purchase") return MASTER_89_REPORTS.find((r) => r.key === "stock-movement-ledger") || MASTER_89_REPORTS[36];
    if (type === "payments") return MASTER_89_REPORTS.find((r) => r.key === "payment-collection-report") || MASTER_89_REPORTS[45];
    if (type === "refunds") return MASTER_89_REPORTS.find((r) => r.key === "refund-report") || MASTER_89_REPORTS[47];
    if (type === "tax") return MASTER_89_REPORTS.find((r) => r.key === "tax-report") || MASTER_89_REPORTS[49];
    if (type === "marketing") return MASTER_89_REPORTS.find((r) => r.key === "coupon-performance-report") || MASTER_89_REPORTS[75];
    if (type === "shipping") return MASTER_89_REPORTS.find((r) => r.key === "shipment-report") || MASTER_89_REPORTS[53];
    return null;
  }, [type]);

  // Handle switching report from the dropdown
  const handleReportTypeChange = (e) => {
    const val = e.target.value;
    setSelectedReportType(val);
    const target = REPORT_TYPES.find((r) => r.label === val);
    if (target) {
      navigate(target.path);
    }
  };

  // Reset Filters
  const handleResetFilters = () => {
    setDateRange("01 Oct 2026 - 07 Oct 2026");
    setGroupBy("Daily");
    setCategory("All Categories");
    setSeller("All Sellers");
    setCustomer("All Customers");
    setProduct("All Products");
    setPaymentMethod("All Payment Methods");
    setOrderStatus("All Status");
    setShippingZone("All Zones");
    setCountry("All Countries");
    setStateRegion("All States");
    toast.success("Filters reset to default");
  };

  const handleGenerateReport = () => {
    toast.success(`Generated ${selectedReportType} for ${dateRange}`);
  };

  const handleSaveFilter = () => {
    toast.success("Filter preference saved successfully");
  };

  // Export handlers
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    // Summary Sheet
    const summaryData = [
      ["Metric", "Value", "Period Comparison"],
      ["Total Sales", "₹ 18,56,320", "+18% vs previous period"],
      ["Total Orders", "1,245", "+12% vs previous period"],
      ["Total Customers", "892", "+10% vs previous period"],
      ["Total Products Sold", "3,428", "+15% vs previous period"],
      ["Total Sellers", "156", "+8% vs previous period"],
      ["Total Commission", "₹ 2,45,380", "+14% vs previous period"],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, "Overview");

    // Top Products Sheet
    const productsData = [
      ["#", "Product Name", "Category", "Qty Sold", "Sales Amount (₹)", "% of Total"],
      ...TOP_PRODUCTS.map((p) => [p.id, p.name, p.category, p.qty, p.amount, p.pct]),
    ];
    const wsProducts = XLSX.utils.aoa_to_sheet(productsData);
    XLSX.utils.book_append_sheet(wb, wsProducts, "Top Products");

    // Top Sellers Sheet
    const sellersData = [
      ["#", "Seller / Artisan", "Orders", "Sales Amount (₹)", "Commission (₹)"],
      ...TOP_SELLERS.map((s) => [s.id, s.name, s.orders, s.sales, s.comm]),
    ];
    const wsSellers = XLSX.utils.aoa_to_sheet(sellersData);
    XLSX.utils.book_append_sheet(wb, wsSellers, "Top Sellers");

    XLSX.writeFile(wb, `Bharat_Karigari_${selectedReportType.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Excel report exported successfully");
    setExportDropdownOpen(false);
  };

  const handleExportCSV = () => {
    let csv = "Product Name,Category,Qty Sold,Sales Amount,Share\n";
    TOP_PRODUCTS.forEach((p) => {
      csv += `"${p.name}","${p.category}",${p.qty},"₹ ${p.amount}","${p.pct}"\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Bharat_Karigari_${selectedReportType.replace(/\s+/g, "_")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV file downloaded successfully");
    setExportDropdownOpen(false);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.setTextColor(176, 125, 23); // #b07d17 theme
    doc.text("Bharat Karigari - Reports Dashboard", 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleDateString("en-IN")} | Period: ${dateRange}`, 14, 28);

    doc.autoTable({
      startY: 35,
      head: [["Metric", "Value", "Growth"]],
      body: [
        ["Total Sales", "₹ 18,56,320", "+18%"],
        ["Total Orders", "1,245", "+12%"],
        ["Total Customers", "892", "+10%"],
        ["Total Products Sold", "3,428", "+15%"],
        ["Total Sellers", "156", "+8%"],
        ["Total Commission", "₹ 2,45,380", "+14%"],
      ],
      headStyles: { fillColor: [176, 125, 23] },
    });

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 12,
      head: [["#", "Product Name", "Category", "Qty", "Sales (₹)", "% Share"]],
      body: TOP_PRODUCTS.map((p) => [p.id, p.name, p.category, p.qty, p.amount, p.pct]),
      headStyles: { fillColor: [19, 25, 33] },
    });

    doc.save(`Bharat_Karigari_${selectedReportType.replace(/\s+/g, "_")}.pdf`);
    toast.success("PDF report generated successfully");
    setExportDropdownOpen(false);
  };

  /* ---------------------------------------------------------------- */
  /*  CHART CONFIGURATIONS                                            */
  /* ---------------------------------------------------------------- */

  // 1. Sales & Orders Trend (Bar + Line Combo)
  // Theme color for Sales Bar: #c59a3f (Warm artisan gold), Line: #10b981 (Emerald)
  const salesOrdersSeries = [
    {
      name: "Sales (₹)",
      type: "column",
      data: [195000, 240000, 285000, 340000, 360000, 420000, 480000],
    },
    {
      name: "Orders",
      type: "line",
      data: [85, 105, 130, 145, 175, 190, 210],
    },
  ];

  const salesOrdersOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "inherit",
    },
    stroke: {
      width: [0, 3],
      curve: "smooth",
    },
    colors: ["#c59a3f", "#10b981"], // Theme Gold & Emerald (NO BLUE!)
    plotOptions: {
      bar: {
        columnWidth: "48%",
        borderRadius: 4,
      },
    },
    fill: {
      opacity: [0.95, 1],
    },
    markers: {
      size: [0, 5],
      colors: ["#10b981"],
      strokeColors: "#fff",
      strokeWidth: 2,
      hover: { size: 7 },
    },
    xaxis: {
      categories: ["01 Oct", "02 Oct", "03 Oct", "04 Oct", "05 Oct", "06 Oct", "07 Oct"],
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: { colors: "#64748b", fontSize: "11px", fontWeight: 500 },
      },
    },
    yaxis: [
      {
        min: 0,
        max: 600000,
        tickAmount: 6,
        labels: {
          style: { colors: "#64748b", fontSize: "11px" },
          formatter: (val) => `₹ ${(val / 100000).toFixed(1)}L`,
        },
      },
      {
        opposite: true,
        min: 0,
        max: 250,
        tickAmount: 5,
        labels: {
          style: { colors: "#64748b", fontSize: "11px" },
          formatter: (val) => `${val}`,
        },
      },
    ],
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 2,
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      fontSize: "12px",
      markers: { width: 10, height: 10, radius: 3 },
      labels: { colors: "#475569" },
    },
    tooltip: {
      theme: "light",
      shared: true,
      intersect: false,
      y: {
        formatter: (val, { seriesIndex }) =>
          seriesIndex === 0 ? `₹ ${val.toLocaleString("en-IN")}` : `${val} Orders`,
      },
    },
  };

  // 2. Sales by Category (Donut)
  const categorySeries = CATEGORY_SALES.map((c) => c.pct);
  const categoryOptions = {
    chart: { type: "donut", fontFamily: "inherit" },
    labels: CATEGORY_SALES.map((c) => c.name),
    colors: CATEGORY_SALES.map((c) => c.color),
    dataLabels: { enabled: false },
    legend: { show: false },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
          labels: {
            show: true,
            name: { show: false },
            value: { show: false },
            total: {
              show: true,
              showAlways: true,
              label: "Total Sales",
              fontSize: "12px",
              color: "#64748b",
              formatter: () => "₹ 18,56,320",
            },
          },
        },
      },
    },
    stroke: { width: 2, colors: ["#ffffff"] },
    tooltip: {
      y: { formatter: (val) => `${val}% of Total Sales` },
    },
  };

  // 3. Sales by Payment Method (Donut)
  const paymentSeries = PAYMENT_METHODS.map((p) => p.pct);
  const paymentOptions = {
    chart: { type: "donut", fontFamily: "inherit" },
    labels: PAYMENT_METHODS.map((p) => p.name),
    colors: PAYMENT_METHODS.map((p) => p.color),
    dataLabels: { enabled: false },
    legend: { show: false },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
          labels: {
            show: true,
            name: { show: false },
            value: { show: false },
            total: {
              show: true,
              showAlways: true,
              label: "Total Sales",
              fontSize: "12px",
              color: "#64748b",
              formatter: () => "₹ 18,56,320",
            },
          },
        },
      },
    },
    stroke: { width: 2, colors: ["#ffffff"] },
    tooltip: {
      y: { formatter: (val) => `${val}% of Total Volume` },
    },
  };

  // 4. Order Status Distribution (Donut)
  const orderStatusSeries = ORDER_STATUS_DIST.map((s) => s.pct);
  const orderStatusOptions = {
    chart: { type: "donut", fontFamily: "inherit" },
    labels: ORDER_STATUS_DIST.map((s) => s.label),
    colors: ORDER_STATUS_DIST.map((s) => s.color),
    dataLabels: { enabled: false },
    legend: { show: false },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            name: { show: false },
            value: { show: false },
            total: {
              show: true,
              showAlways: true,
              label: "Total Orders",
              fontSize: "11px",
              color: "#64748b",
              formatter: () => "1,245",
            },
          },
        },
      },
    },
    stroke: { width: 2, colors: ["#ffffff"] },
  };

  // 5. Monthly Comparison (Bar + Line)
  const monthlySeries = [
    {
      name: "Sales (₹)",
      type: "column",
      data: [1100000, 1350000, 1420000, 1680000, 1750000, 1856320],
    },
    {
      name: "Orders",
      type: "line",
      data: [450, 560, 620, 710, 820, 1245],
    },
  ];

  const monthlyOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "inherit",
    },
    stroke: { width: [0, 2.5], curve: "smooth" },
    colors: ["#c59a3f", "#10b981"], // Theme Gold and Emerald
    plotOptions: {
      bar: { columnWidth: "45%", borderRadius: 4 },
    },
    xaxis: {
      categories: ["May", "Jun", "Jul", "Aug", "Sep", "Oct"],
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: "#64748b", fontSize: "11px" } },
    },
    yaxis: [
      {
        min: 0,
        max: 2000000,
        tickAmount: 4,
        labels: {
          style: { colors: "#64748b", fontSize: "10px" },
          formatter: (v) => `₹ ${(v / 100000).toFixed(0)}L`,
        },
      },
      {
        opposite: true,
        min: 0,
        max: 1400,
        tickAmount: 4,
        labels: {
          style: { colors: "#64748b", fontSize: "10px" },
          formatter: (v) => `${v}`,
        },
      },
    ],
    grid: { borderColor: "#f1f5f9", strokeDashArray: 2 },
    legend: {
      position: "top",
      horizontalAlign: "right",
      fontSize: "11px",
      markers: { width: 8, height: 8, radius: 2 },
      labels: { colors: "#64748b" },
    },
    tooltip: {
      theme: "light",
      shared: true,
      intersect: false,
      y: {
        formatter: (val, { seriesIndex }) =>
          seriesIndex === 0 ? `₹ ${val.toLocaleString("en-IN")}` : `${val} Orders`,
      },
    },
  };

  if (selectedCatalogReport) {
    return (
      <StandardReportScreen
        report={selectedCatalogReport}
        onBackToDashboard={() => setSelectedCatalogReport(null)}
        onSelectOtherReport={() => {
          setSelectedCatalogReport(null);
          setActiveCatalogView(true);
        }}
      />
    );
  }

  if (activeCatalogView || type === "custom") {
    return (
      <ReportCatalogBrowser
        onSelectReport={(rep) => setSelectedCatalogReport(rep)}
        onOpenDashboard={() => {
          setActiveCatalogView(false);
          if (type === "custom") navigate("/admin-reports");
        }}
      />
    );
  }

  if (matchedReport) {
    return (
      <StandardReportScreen
        report={matchedReport}
        onBackToDashboard={() => navigate("/admin-reports")}
        onSelectOtherReport={() => setActiveCatalogView(true)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-800 pb-16">
      {/* ── TOP NAV BAR CONTROLS / BREADCRUMB ──────────────────────── */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-xs">
        <div className="max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Left: Breadcrumbs & Title */}
            <div>
              <div className="flex items-center gap-2 text-xs text-slate-400 font-medium mb-1">
                <span
                  onClick={() => navigate("/dashboard")}
                  className="hover:text-slate-700 cursor-pointer transition-colors"
                >
                  Dashboard
                </span>
                <span>/</span>
                <span className="text-[#b07d17] font-semibold">Reports</span>
                {currentReportType !== "dashboard" && (
                  <>
                    <span>/</span>
                    <span className="text-slate-700 font-semibold">{selectedReportType}</span>
                  </>
                )}
              </div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2.5">
                <span>{selectedReportType}</span>
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-amber-50 text-[#b07d17] border border-amber-200 font-medium">
                  Live Analytics
                </span>
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Get detailed insights about your business. Analyze sales, orders, customers, inventory, payments and more.
              </p>
            </div>

            {/* Right: Date Range Pills & Export Button */}
            <div className="flex items-center gap-2.5 flex-wrap">
              {/* Preset Range Selector */}
              <div className="inline-flex items-center bg-slate-100 p-1 rounded-lg border border-slate-200 text-xs font-medium">
                {["Today", "Last 7 Days", "Last 30 Days", "This Month"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveDateTab(tab)}
                    className={`px-2.5 py-1 rounded-md transition-all ${
                      activeDateTab === tab
                        ? "bg-white text-slate-900 shadow-xs font-semibold"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Date Display Pill */}
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-700 shadow-xs">
                <Calendar size={14} className="text-[#b07d17]" />
                <span>{dateRange}</span>
              </div>

              {/* All 89 Reports Catalog Button */}
              <button
                onClick={() => setActiveCatalogView(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 border border-amber-200 text-xs font-bold text-[#b07d17] transition-all cursor-pointer shadow-2xs"
              >
                <FolderOpen size={14} />
                <span>All 89 Reports Catalog</span>
              </button>

              {/* Export Button (THEME COLOR MATCH: Gold/Amber/Bronze instead of generic blue!) */}
              <div className="relative">
                <button
                  onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold text-slate-900 bg-gradient-to-r from-[#c59a3f] via-[#e5c158] to-[#d4af37] hover:brightness-105 active:scale-95 shadow-sm shadow-[#d4af37]/30 border border-[#f3d57b] transition-all cursor-pointer"
                >
                  <Download size={15} className="text-slate-900 stroke-[2.2]" />
                  <span>Export Report</span>
                  <ChevronDown size={14} className="text-slate-900" />
                </button>

                {/* Export Dropdown Menu */}
                {exportDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-xl z-30 p-1.5 animate-in fade-in slide-in-from-top-2 duration-150">
                    <div className="px-3 py-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                      Export Options
                    </div>
                    <button
                      onClick={handleExportExcel}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 rounded-lg transition-colors text-left"
                    >
                      <FileSpreadsheet size={15} className="text-emerald-600" />
                      <span>Export as Excel (.xlsx)</span>
                    </button>
                    <button
                      onClick={handleExportPDF}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-rose-50 hover:text-rose-700 rounded-lg transition-colors text-left"
                    >
                      <FileText size={15} className="text-rose-600" />
                      <span>Export as PDF (.pdf)</span>
                    </button>
                    <button
                      onClick={handleExportCSV}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-amber-50 hover:text-amber-800 rounded-lg transition-colors text-left"
                    >
                      <Download size={15} className="text-[#b07d17]" />
                      <span>Export as CSV (.csv)</span>
                    </button>
                    <div className="my-1 border-t border-slate-100" />
                    <button
                      onClick={() => {
                        window.print();
                        setExportDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-left"
                    >
                      <Printer size={15} className="text-slate-500" />
                      <span>Print Summary</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-8 pt-5 space-y-5">
        {/* ── 1. TOP 6 KPI METRIC CARDS ─────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
          {KPI_DATA.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <div
                key={kpi.id}
                className="bg-white rounded-xl p-3.5 sm:p-4 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-200 relative overflow-hidden group"
              >
                <div className="flex items-center justify-between mb-2">
                  <div
                    className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shadow-xs transition-transform group-hover:scale-105 ${kpi.iconBg}`}
                  >
                    <Icon size={18} className="stroke-[2.2]" />
                  </div>
                  <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                    <TrendingUp size={11} className="stroke-[2.5]" />
                    {kpi.change}
                  </span>
                </div>

                <p className="text-[11.5px] font-medium text-slate-500 tracking-wide truncate">
                  {kpi.label}
                </p>
                <p className="text-lg sm:text-xl font-bold text-slate-900 mt-0.5 tracking-tight">
                  {kpi.value}
                </p>
                <p className="text-[10.5px] text-slate-400 mt-1 font-normal">
                  {kpi.subtext}
                </p>
              </div>
            );
          })}
        </div>

        {/* ── 2. REPORT FILTERS PANEL ─────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-xs p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3.5">
            <h2 className="text-sm font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <Filter size={15} className="text-[#b07d17]" />
              <span>Report Filters</span>
            </h2>
            <span className="text-[11px] text-slate-400">
              Narrow down analytical aggregates by 12 multi-dimensional parameters
            </span>
          </div>

          {/* Grid of 6 primary filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            {/* 1. Date Range */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                Date Range
              </label>
              <div className="relative">
                <Calendar
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <input
                  type="text"
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-xs bg-slate-50/60 border border-slate-200 rounded-lg text-slate-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17] focus:border-[#b07d17] transition-all font-medium"
                />
              </div>
            </div>

            {/* 2. Report Type (Dropdown showing all report options!) */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                Report Type
              </label>
              <select
                value={selectedReportType}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "BROWSE_ALL") {
                    setActiveCatalogView(true);
                    return;
                  }
                  handleReportTypeChange(e);
                }}
                className="w-full px-3 py-2 text-xs bg-slate-50/60 border border-slate-200 rounded-lg text-slate-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17] focus:border-[#b07d17] transition-all font-medium cursor-pointer"
              >
                <optgroup label="Featured Dashboard Views">
                  {REPORT_TYPES.map((rt) => (
                    <option key={rt.id} value={rt.label}>
                      {rt.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Complete Catalog">
                  <option value="BROWSE_ALL">★ Browse All 89 Reports Catalog...</option>
                </optgroup>
              </select>
            </div>

            {/* 3. Group By */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                Group By
              </label>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="w-full px-3 py-2 text-xs bg-slate-50/60 border border-slate-200 rounded-lg text-slate-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17] focus:border-[#b07d17] transition-all font-medium cursor-pointer"
              >
                <option value="Daily">Daily</option>
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
                <option value="Yearly">Yearly</option>
              </select>
            </div>

            {/* 4. Category */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 text-xs bg-slate-50/60 border border-slate-200 rounded-lg text-slate-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17] focus:border-[#b07d17] transition-all font-medium cursor-pointer"
              >
                <option value="All Categories">All Categories</option>
                <option value="Handloom">Handloom</option>
                <option value="Home Decor">Home Decor</option>
                <option value="Textiles">Textiles</option>
                <option value="Pottery">Pottery</option>
                <option value="Metal Crafts">Metal Crafts</option>
                <option value="Wood Crafts">Wood Crafts</option>
                <option value="Stone Crafts">Stone Crafts</option>
              </select>
            </div>

            {/* 5. Seller / Artisan */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                Seller / Artisan
              </label>
              <select
                value={seller}
                onChange={(e) => setSeller(e.target.value)}
                className="w-full px-3 py-2 text-xs bg-slate-50/60 border border-slate-200 rounded-lg text-slate-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17] focus:border-[#b07d17] transition-all font-medium cursor-pointer"
              >
                <option value="All Sellers">All Sellers</option>
                <option value="Odisha Handlooms">Odisha Handlooms</option>
                <option value="Rajasthan Crafts">Rajasthan Crafts</option>
                <option value="Assam Weaves">Assam Weaves</option>
                <option value="Tribal Artisans">Tribal Artisans</option>
                <option value="North East Crafts">North East Crafts</option>
              </select>
            </div>

            {/* 6. Customer */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                Customer
              </label>
              <select
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                className="w-full px-3 py-2 text-xs bg-slate-50/60 border border-slate-200 rounded-lg text-slate-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17] focus:border-[#b07d17] transition-all font-medium cursor-pointer"
              >
                <option value="All Customers">All Customers</option>
                <option value="Priya Sharma">Priya Sharma</option>
                <option value="Rahul Mehta">Rahul Mehta</option>
                <option value="Anita Desai">Anita Desai</option>
                <option value="Vikram Singh">Vikram Singh</option>
                <option value="Neha Kapoor">Neha Kapoor</option>
              </select>
            </div>
          </div>

          {/* Row 2 of Filters (Product, Payment Method, Order Status, Shipping Zone, Country, State/Region) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mt-3">
            {/* 7. Product */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                Product
              </label>
              <select
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                className="w-full px-3 py-2 text-xs bg-slate-50/60 border border-slate-200 rounded-lg text-slate-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17] focus:border-[#b07d17] transition-all font-medium cursor-pointer"
              >
                <option value="All Products">All Products</option>
                <option value="Handwoven Cotton Saree">Handwoven Cotton Saree</option>
                <option value="Terracotta Diya Set (6 pcs)">Terracotta Diya Set</option>
                <option value="Wooden Wall Art">Wooden Wall Art</option>
                <option value="Ikat Dupatta">Ikat Dupatta</option>
                <option value="Brass Pooja Diya Set">Brass Pooja Diya Set</option>
              </select>
            </div>

            {/* 8. Payment Method */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                Payment Method
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full px-3 py-2 text-xs bg-slate-50/60 border border-slate-200 rounded-lg text-slate-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17] focus:border-[#b07d17] transition-all font-medium cursor-pointer"
              >
                <option value="All Payment Methods">All Payment Methods</option>
                <option value="UPI">UPI</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Debit Card">Debit Card</option>
                <option value="Net Banking">Net Banking</option>
                <option value="COD">COD</option>
                <option value="Wallet">Wallet</option>
                <option value="EMI / Pay Later">EMI / Pay Later</option>
              </select>
            </div>

            {/* 9. Order Status */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                Order Status
              </label>
              <select
                value={orderStatus}
                onChange={(e) => setOrderStatus(e.target.value)}
                className="w-full px-3 py-2 text-xs bg-slate-50/60 border border-slate-200 rounded-lg text-slate-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17] focus:border-[#b07d17] transition-all font-medium cursor-pointer"
              >
                <option value="All Status">All Status</option>
                <option value="Delivered">Delivered</option>
                <option value="Shipped">Shipped</option>
                <option value="Processing">Processing</option>
                <option value="Pending">Pending</option>
                <option value="Cancelled">Cancelled</option>
                <option value="Returned">Returned</option>
              </select>
            </div>

            {/* 10. Shipping Zone */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                Shipping Zone
              </label>
              <select
                value={shippingZone}
                onChange={(e) => setShippingZone(e.target.value)}
                className="w-full px-3 py-2 text-xs bg-slate-50/60 border border-slate-200 rounded-lg text-slate-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17] focus:border-[#b07d17] transition-all font-medium cursor-pointer"
              >
                <option value="All Zones">All Zones</option>
                <option value="North Zone">North Zone</option>
                <option value="East Zone">East Zone</option>
                <option value="West Zone">West Zone</option>
                <option value="South Zone">South Zone</option>
                <option value="Central Zone">Central Zone</option>
              </select>
            </div>

            {/* 11. Country */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                Country
              </label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-3 py-2 text-xs bg-slate-50/60 border border-slate-200 rounded-lg text-slate-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17] focus:border-[#b07d17] transition-all font-medium cursor-pointer"
              >
                <option value="All Countries">All Countries</option>
                <option value="India">India</option>
                <option value="International">International</option>
              </select>
            </div>

            {/* 12. State / Region */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                State / Region
              </label>
              <select
                value={stateRegion}
                onChange={(e) => setStateRegion(e.target.value)}
                className="w-full px-3 py-2 text-xs bg-slate-50/60 border border-slate-200 rounded-lg text-slate-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17] focus:border-[#b07d17] transition-all font-medium cursor-pointer"
              >
                <option value="All States">All States</option>
                <option value="Odisha">Odisha</option>
                <option value="Maharashtra">Maharashtra</option>
                <option value="Delhi">Delhi</option>
                <option value="Karnataka">Karnataka</option>
                <option value="Tamil Nadu">Tamil Nadu</option>
                <option value="West Bengal">West Bengal</option>
                <option value="Uttar Pradesh">Uttar Pradesh</option>
                <option value="Rajasthan">Rajasthan</option>
              </select>
            </div>
          </div>

          {/* Filter Footer Action Row */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-3.5 mt-3.5 border-t border-slate-100">
            {/* Advanced Filters Toggle */}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#b07d17] hover:text-[#8c6312] transition-colors cursor-pointer"
            >
              <SlidersHorizontal size={13} />
              <span>{showAdvanced ? "Hide Advanced Filters" : "Advanced Filters"}</span>
              {showAdvanced ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>

            {/* Action Buttons: Reset, Save Filter, Generate Report */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleResetFilters}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors cursor-pointer"
              >
                <RotateCcw size={13} />
                <span>Reset</span>
              </button>

              <button
                onClick={handleSaveFilter}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors cursor-pointer"
              >
                <Bookmark size={13} />
                <span>Save Filter</span>
              </button>

              {/* Generate Report Button (THEME COLOR MATCH: Warm Gold/Artisan instead of bright blue!) */}
              <button
                onClick={handleGenerateReport}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold text-slate-900 bg-gradient-to-r from-[#c59a3f] via-[#e5c158] to-[#d4af37] hover:brightness-105 active:scale-95 shadow-sm shadow-[#d4af37]/30 border border-[#f3d57b] transition-all cursor-pointer"
              >
                <BarChart3 size={14} className="stroke-[2.2]" />
                <span>Generate Report</span>
              </button>
            </div>
          </div>

          {/* Advanced Filters Drawer */}
          {showAdvanced && (
            <div className="mt-3.5 pt-3.5 border-t border-dashed border-slate-200 grid grid-cols-1 sm:grid-cols-3 gap-3 animate-in fade-in duration-150">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                  Min Order Value (₹)
                </label>
                <input
                  type="number"
                  placeholder="e.g. 500"
                  className="w-full px-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                  Max Order Value (₹)
                </label>
                <input
                  type="number"
                  placeholder="e.g. 50000"
                  className="w-full px-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                  SKU or Barcode Search
                </label>
                <input
                  type="text"
                  placeholder="Search specific SKU code..."
                  className="w-full px-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700"
                />
              </div>
            </div>
          )}
        </div>

        {/* ── SPECIFIC SUB-REPORT DETAILED VIEW (IF SUB-REPORT ACTIVE) ── */}
        {currentReportType !== "dashboard" && (
          <ReportDetailView type={currentReportType} />
        )}

        {/* ── 3. MIDDLE CHARTS ROW (3 COLUMNS) ───────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Chart 1: Sales & Orders Trend (6 cols) */}
          <div className="lg:col-span-6 bg-white rounded-xl border border-slate-200/80 shadow-xs p-4 sm:p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-bold text-slate-900 tracking-tight">
                  Sales & Orders Trend
                </h3>
                <p className="text-[11px] text-slate-400">
                  Daily revenue vs total order volume trajectory
                </p>
              </div>
              <div className="flex items-center gap-3 text-xs font-medium">
                <span className="flex items-center gap-1.5 text-slate-600">
                  <span className="w-2.5 h-2.5 rounded-xs bg-[#c59a3f]" />
                  Sales (₹)
                </span>
                <span className="flex items-center gap-1.5 text-slate-600">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#10b981]" />
                  Orders
                </span>
              </div>
            </div>

            <div className="w-full h-[280px]">
              <Chart
                options={salesOrdersOptions}
                series={salesOrdersSeries}
                type="line"
                height="100%"
                width="100%"
              />
            </div>
          </div>

          {/* Chart 2: Sales by Category (3 cols) */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200/80 shadow-xs p-4 sm:p-5 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-900 tracking-tight">
                Sales by Category
              </h3>
              <span className="text-[11px] text-slate-400">Share %</span>
            </div>

            <div className="relative h-[190px] flex items-center justify-center my-1">
              <Chart
                options={categoryOptions}
                series={categorySeries}
                type="donut"
                height="100%"
                width="100%"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xs font-bold text-slate-900">₹ 18,56,320</span>
                <span className="text-[10px] text-slate-400 font-medium">Total Sales</span>
              </div>
            </div>

            <div className="mt-2 space-y-1.5 overflow-y-auto max-h-[120px] pr-1">
              {CATEGORY_SALES.map((cat) => (
                <div key={cat.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: cat.color }}
                    />
                    <span className="text-slate-600 truncate max-w-[100px]">{cat.name}</span>
                  </div>
                  <span className="font-semibold text-slate-800">{cat.pct}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Chart 3: Sales by Payment Method (3 cols) */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200/80 shadow-xs p-4 sm:p-5 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-900 tracking-tight">
                Sales by Payment Method
              </h3>
              <span className="text-[11px] text-slate-400">Channels</span>
            </div>

            <div className="relative h-[190px] flex items-center justify-center my-1">
              <Chart
                options={paymentOptions}
                series={paymentSeries}
                type="donut"
                height="100%"
                width="100%"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xs font-bold text-slate-900">₹ 18,56,320</span>
                <span className="text-[10px] text-slate-400 font-medium">Total Sales</span>
              </div>
            </div>

            <div className="mt-2 space-y-1.5 overflow-y-auto max-h-[120px] pr-1">
              {PAYMENT_METHODS.map((pay) => (
                <div key={pay.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: pay.color }}
                    />
                    <span className="text-slate-600 truncate max-w-[100px]">{pay.name}</span>
                  </div>
                  <span className="font-semibold text-slate-800">{pay.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── 4. TABLES ROW (3 TABLES: PRODUCTS, SELLERS, CUSTOMERS) ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Table 1: Top Selling Products (5 cols) */}
          <div className="lg:col-span-5 bg-white rounded-xl border border-slate-200/80 shadow-xs p-4 sm:p-5 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-900 tracking-tight">
                Top Selling Products
              </h3>
              <button
                onClick={() => navigate("/admin-products")}
                className="text-xs font-semibold text-[#b07d17] hover:text-[#8c6312] inline-flex items-center gap-1 transition-colors"
              >
                <span>View All</span>
                <ArrowRight size={12} />
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    <th className="pb-2 pl-1 w-6">#</th>
                    <th className="pb-2">Product Name</th>
                    <th className="pb-2">Category</th>
                    <th className="pb-2 text-right">Qty</th>
                    <th className="pb-2 text-right">Sales (₹)</th>
                    <th className="pb-2 text-right pr-1">% Share</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 text-xs">
                  {TOP_PRODUCTS.map((prod) => (
                    <tr key={prod.id} className="hover:bg-slate-50/70 transition-colors">
                      <td className="py-2.5 pl-1 text-slate-400 font-medium">{prod.id}</td>
                      <td className="py-2.5">
                        <div className="flex items-center gap-2">
                          <img
                            src={prod.img}
                            alt={prod.name}
                            className="w-7 h-7 rounded-md object-cover border border-slate-200"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                          <span className="font-semibold text-slate-800 line-clamp-1 max-w-[140px]">
                            {prod.name}
                          </span>
                        </div>
                      </td>
                      <td className="py-2.5 text-slate-500 font-medium">{prod.category}</td>
                      <td className="py-2.5 text-right font-semibold text-slate-700">
                        {prod.qty}
                      </td>
                      <td className="py-2.5 text-right font-bold text-slate-900">
                        {prod.amount}
                      </td>
                      <td className="py-2.5 text-right pr-1 font-semibold text-[#b07d17]">
                        {prod.pct}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Table 2: Top Sellers / Artisans (4 cols) */}
          <div className="lg:col-span-4 bg-white rounded-xl border border-slate-200/80 shadow-xs p-4 sm:p-5 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-900 tracking-tight">
                Top Sellers / Artisans
              </h3>
              <button
                onClick={() => navigate("/admin-sellers")}
                className="text-xs font-semibold text-[#b07d17] hover:text-[#8c6312] inline-flex items-center gap-1 transition-colors"
              >
                <span>View All</span>
                <ArrowRight size={12} />
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    <th className="pb-2 pl-1 w-6">#</th>
                    <th className="pb-2">Seller / Artisan</th>
                    <th className="pb-2 text-right">Orders</th>
                    <th className="pb-2 text-right">Sales (₹)</th>
                    <th className="pb-2 text-right pr-1">Commission (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 text-xs">
                  {TOP_SELLERS.map((seller) => (
                    <tr key={seller.id} className="hover:bg-slate-50/70 transition-colors">
                      <td className="py-2.5 pl-1 text-slate-400 font-medium">{seller.id}</td>
                      <td className="py-2.5 font-semibold text-slate-800">
                        {seller.name}
                      </td>
                      <td className="py-2.5 text-right font-semibold text-slate-700">
                        {seller.orders}
                      </td>
                      <td className="py-2.5 text-right font-bold text-slate-900">
                        {seller.sales}
                      </td>
                      <td className="py-2.5 text-right pr-1 font-semibold text-emerald-700">
                        {seller.comm}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Table 3: Top Customers (3 cols) */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200/80 shadow-xs p-4 sm:p-5 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-900 tracking-tight">
                Top Customers
              </h3>
              <button
                onClick={() => navigate("/admin-customers")}
                className="text-xs font-semibold text-[#b07d17] hover:text-[#8c6312] inline-flex items-center gap-1 transition-colors"
              >
                <span>View All</span>
                <ArrowRight size={12} />
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    <th className="pb-2 pl-1 w-6">#</th>
                    <th className="pb-2">Customer</th>
                    <th className="pb-2 text-right">Orders</th>
                    <th className="pb-2 text-right pr-1">Spent (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 text-xs">
                  {TOP_CUSTOMERS.map((cust) => (
                    <tr key={cust.id} className="hover:bg-slate-50/70 transition-colors">
                      <td className="py-2.5 pl-1 text-slate-400 font-medium">{cust.id}</td>
                      <td className="py-2.5 font-semibold text-slate-800 truncate max-w-[110px]">
                        {cust.name}
                      </td>
                      <td className="py-2.5 text-right font-semibold text-slate-700">
                        {cust.orders}
                      </td>
                      <td className="py-2.5 text-right pr-1 font-bold text-slate-900">
                        {cust.spent}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── 5. BOTTOM ROW: STATUS, REGION, MONTHLY, QUICK EXPORTS ──── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4">
          {/* Panel 1: Order Status Distribution (3 cols) */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200/80 shadow-xs p-4 sm:p-5 flex flex-col">
            <h3 className="text-sm font-bold text-slate-900 tracking-tight mb-2">
              Order Status Distribution
            </h3>

            <div className="relative h-[160px] flex items-center justify-center my-1">
              <Chart
                options={orderStatusOptions}
                series={orderStatusSeries}
                type="donut"
                height="100%"
                width="100%"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xs font-bold text-slate-900">1,245</span>
                <span className="text-[10px] text-slate-400">Total Orders</span>
              </div>
            </div>

            <div className="mt-2 space-y-1.5">
              {ORDER_STATUS_DIST.map((item) => (
                <div key={item.label} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-slate-600">{item.label}</span>
                  </div>
                  <span className="font-semibold text-slate-800">{item.pct}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Panel 2: Orders by Region (3 cols) */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200/80 shadow-xs p-4 sm:p-5 flex flex-col">
            <h3 className="text-sm font-bold text-slate-900 tracking-tight mb-2">
              Orders by Region
            </h3>

            {/* Stylized map indicator */}
            <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-amber-50/50 border border-amber-100 text-[11px] text-[#b07d17]">
              <MapPin size={14} className="shrink-0 text-[#b07d17]" />
              <span>Odisha & Western region lead customer volume</span>
            </div>

            <div className="space-y-2 mt-1 flex-1">
              {REGION_ORDERS.map((reg) => (
                <div key={reg.state} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-slate-700">{reg.state}</span>
                    <span className="font-semibold text-slate-900">{reg.pct}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#b07d17] to-[#e5c158] rounded-full"
                      style={{ width: `${reg.pct * 2}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Panel 3: Monthly Comparison (3 cols) */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200/80 shadow-xs p-4 sm:p-5 flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-bold text-slate-900 tracking-tight">
                Monthly Comparison
              </h3>
              <div className="flex items-center gap-2 text-[10px]">
                <span className="flex items-center gap-1 text-slate-500">
                  <span className="w-2 h-2 rounded-xs bg-[#c59a3f]" />
                  Sales
                </span>
                <span className="flex items-center gap-1 text-slate-500">
                  <span className="w-2 h-2 rounded-full bg-[#10b981]" />
                  Orders
                </span>
              </div>
            </div>

            <div className="w-full h-[230px]">
              <Chart
                options={monthlyOptions}
                series={monthlySeries}
                type="line"
                height="100%"
                width="100%"
              />
            </div>
          </div>

          {/* Panel 4: Export Reports Quick Actions (3 cols) */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200/80 shadow-xs p-4 sm:p-5 flex flex-col justify-between">
            <div>
              <h3 className="text-sm font-bold text-slate-900 tracking-tight mb-1">
                Export Reports
              </h3>
              <p className="text-[11px] text-slate-400 mb-3">
                Download verified audit files or automate recurring deliveries
              </p>

              <div className="space-y-2">
                <button
                  onClick={handleExportExcel}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all text-xs font-semibold text-slate-700 hover:text-emerald-800"
                >
                  <span className="flex items-center gap-2.5">
                    <FileSpreadsheet size={16} className="text-emerald-600" />
                    <span>Export as Excel</span>
                  </span>
                  <span className="text-[10px] text-slate-400 font-normal">.xlsx</span>
                </button>

                <button
                  onClick={handleExportPDF}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-lg border border-slate-200 hover:border-rose-300 hover:bg-rose-50/50 transition-all text-xs font-semibold text-slate-700 hover:text-rose-800"
                >
                  <span className="flex items-center gap-2.5">
                    <FileText size={16} className="text-rose-600" />
                    <span>Export as PDF</span>
                  </span>
                  <span className="text-[10px] text-slate-400 font-normal">.pdf</span>
                </button>

                <button
                  onClick={handleExportCSV}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-lg border border-slate-200 hover:border-amber-300 hover:bg-amber-50/50 transition-all text-xs font-semibold text-slate-700 hover:text-amber-800"
                >
                  <span className="flex items-center gap-2.5">
                    <Download size={16} className="text-[#b07d17]" />
                    <span>Export as CSV</span>
                  </span>
                  <span className="text-[10px] text-slate-400 font-normal">.csv</span>
                </button>

                <button
                  onClick={() => setScheduleModalOpen(true)}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/40 transition-all text-xs font-semibold text-slate-700 hover:text-blue-800"
                >
                  <span className="flex items-center gap-2.5">
                    <Clock size={16} className="text-blue-600" />
                    <span>Schedule Report</span>
                  </span>
                  <span className="text-[10px] text-slate-400 font-normal">Cron</span>
                </button>

                <button
                  onClick={() => {
                    navigate("/admin-reports/custom");
                    toast.success("Switched to Custom Report builder");
                  }}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-lg border border-slate-200 hover:border-purple-300 hover:bg-purple-50/40 transition-all text-xs font-semibold text-slate-700 hover:text-purple-800"
                >
                  <span className="flex items-center gap-2.5">
                    <Settings size={16} className="text-purple-600" />
                    <span>Custom Report</span>
                  </span>
                  <span className="text-[10px] text-slate-400 font-normal">Builder</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── SCHEDULE REPORT MODAL ──────────────────────────────────── */}
      {scheduleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-[#b07d17] flex items-center justify-center">
                  <Clock size={20} />
                </div>
                <div>
                  <h4 className="text-base font-bold text-slate-900">Schedule Automated Report</h4>
                  <p className="text-xs text-slate-400">Receive periodic reports via email</p>
                </div>
              </div>
            </div>

            <div className="space-y-3.5 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Report Subject</label>
                <input
                  type="text"
                  defaultValue={`${selectedReportType} - Executive Digest`}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-[#b07d17]"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Recipient Email</label>
                <input
                  type="email"
                  placeholder="admin@bharatkarigari.com"
                  defaultValue="admin@bharatkarigari.com"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-[#b07d17]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Frequency</label>
                  <select className="w-full px-3 py-2 border border-slate-200 rounded-lg text-slate-800">
                    <option>Daily (8:00 AM)</option>
                    <option>Weekly (Mondays)</option>
                    <option>Monthly (1st Day)</option>
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Format</label>
                  <select className="w-full px-3 py-2 border border-slate-200 rounded-lg text-slate-800">
                    <option>Excel Workbook (.xlsx)</option>
                    <option>PDF Document (.pdf)</option>
                    <option>CSV Format (.csv)</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-6">
              <button
                onClick={() => setScheduleModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  toast.success("Scheduled automated report successfully!");
                  setScheduleModalOpen(false);
                }}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#c59a3f] via-[#e5c158] to-[#d4af37] text-slate-900 font-bold text-xs shadow-sm hover:brightness-105"
              >
                Confirm Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReportsDashboard;
